import { Alert as NativeAlert, Platform, type AlertButton, type AlertOptions } from 'react-native';

// The app's one Alert. On iOS and Android it is the system alert, unchanged.
// On the web React Native Web's Alert does nothing at all, so every
// "Are you sure?" silently dropped its action and every notice went unseen.
// There the request goes to <AlertHost /> (mounted once in App), which draws
// the dialog in the app's own style with any number of buttons; before it is
// mounted, the browser's own dialogs stand in.

export type AlertRequest = {
  title: string;
  message?: string;
  /** Empty means a single dismiss button, labelled by the host. */
  buttons: AlertButton[];
  options?: AlertOptions;
};

let host: ((request: AlertRequest) => void) | null = null;

/** Called by <AlertHost /> on mount and with null on unmount. */
export function registerAlertHost(next: ((request: AlertRequest) => void) | null): void {
  host = next;
}

type BrowserDialogs = { alert?: (text: string) => void; confirm?: (text: string) => boolean };

/**
 * Without a host: a notice becomes the browser's alert; one action with a
 * cancel becomes its confirm. Several actions are offered one confirm at a
 * time in order, so nothing runs that the person did not accept.
 */
export function browserAlert(request: AlertRequest, dialogs: BrowserDialogs = globalThis as BrowserDialogs): void {
  const text = request.message ? `${request.title}\n\n${request.message}` : request.title;
  const cancel = request.buttons.find((button) => button.style === 'cancel');
  const actions = request.buttons.filter((button) => button.style !== 'cancel');
  if (actions.length === 0) {
    dialogs.alert?.(text);
    (cancel ?? request.buttons[0])?.onPress?.();
    return;
  }
  if (actions.length === 1 && !cancel) {
    dialogs.alert?.(text);
    actions[0].onPress?.();
    return;
  }
  for (const action of actions) {
    const prompt = actions.length === 1 ? text : `${text}\n\n${action.text ?? ''}`;
    if (dialogs.confirm?.(prompt)) { action.onPress?.(); return; }
  }
  if (cancel) cancel.onPress?.();
  else request.options?.onDismiss?.();
}

function webAlert(title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions): void {
  const request: AlertRequest = { title, message, buttons: buttons ?? [], options };
  if (host) host(request);
  else browserAlert(request);
}

// Decided per call, not at import, so one module serves every platform.
// Arguments are passed on exactly as given, so the system alert sees the same call.
export const Alert = {
  alert(...args: Parameters<typeof NativeAlert.alert>): void {
    if (Platform.OS === 'web') webAlert(...args);
    else NativeAlert.alert(...args);
  },
  prompt: (...args: Parameters<typeof NativeAlert.prompt>) => NativeAlert.prompt(...args),
} as typeof NativeAlert;
